// AlexandraPayout.js
import React from "react";
import { Card, CardContent, Typography, Box, Button } from "@mui/material";
import { PieChart, Pie, Cell } from "recharts";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { alpha } from "@mui/material/styles";

const AlexandraPayout = ({ data, startDate, endDate }) => {
  // Filtrăm datele pentru intrările unde achizitorul este  "Alexandra Neculcea"
  const filteredData = data.filter(
    (item) => item.achizitor === "Alexandra Neculcea",
  );
  const totalFilteredProducts = filteredData.length;

  const formatNumber = (number) => {
    return Number(number || 0).toLocaleString("ro-RO", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const formatDate = (value) => {
    if (!value) {
      return "N/A";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString("ro-RO");
  };

  // Inițializăm variabilele
  let totalAmount = 0;
  let numProductsUnder2000 = 0;
  let numProductsOver2000 = 0;
  const productsUnder2000 = [];
  const productsOver2000 = [];

  filteredData.forEach((item) => {
    const pretIntrare = Number(item.pretIntrareRON) || 0;

    if (pretIntrare <= 1999) {
      numProductsUnder2000 += 1;
      productsUnder2000.push(item);
    } else {
      numProductsOver2000 += 1;
      productsOver2000.push(item);
    }
  });

  // Calculăm suma pentru produsele sub sau egal cu 2000 lei
  const amountUnder2000 = numProductsUnder2000 * 20;
  totalAmount += amountUnder2000;

  // Calculăm suma pentru produsele peste 2000 lei conform regulilor pe praguri
  let amountOver2000 = 0;
  let bonusValue = 0;
  let extraProducts = 0;

  if (numProductsOver2000 > 0) {
    if (numProductsOver2000 < 30) {
      bonusValue = 3000;
      amountOver2000 = numProductsOver2000 * 40 + bonusValue;
    } else if (numProductsOver2000 < 50) {
      bonusValue = 3500;
      extraProducts = numProductsOver2000 - 30;
      amountOver2000 = bonusValue + extraProducts * 40;
    } else {
      bonusValue = 6500;
      extraProducts = numProductsOver2000 - 50;
      amountOver2000 = bonusValue + extraProducts * 40;
    }

    totalAmount += amountOver2000;
  }

  const displayStartDate = startDate || "N/A";
  const displayEndDate = endDate || "N/A";

  const currentTierIndex =
    numProductsOver2000 >= 50
      ? 2
      : numProductsOver2000 >= 30
        ? 1
        : numProductsOver2000 > 0
          ? 0
          : -1;
  const nextThreshold =
    numProductsOver2000 >= 50 ? null : numProductsOver2000 >= 30 ? 50 : 30;
  const remainingToNext =
    nextThreshold !== null
      ? Math.max(nextThreshold - numProductsOver2000, 0)
      : 0;

  const tierSegments = [
    {
      title: "Prag 1 (0-29)",
      bonus: `Bonus ${formatNumber(3000)} lei`,
      capacity: 30,
      filled: Math.min(numProductsOver2000, 30),
      color: "#1976d2",
    },
    {
      title: "Prag 2 (30-49)",
      bonus: `Bonus ${formatNumber(3500)} lei`,
      capacity: 20,
      filled: Math.max(Math.min(numProductsOver2000 - 30, 20), 0),
      color: "#2e7d32",
    },
    {
      title: "Prag 3 (50+)",
      bonus: `Bonus ${formatNumber(6500)} lei`,
      capacity: 20,
      filled: Math.max(numProductsOver2000 - 50, 0),
      color: "#ef6c00",
    },
  ];

  const generatePdfReport = () => {
    if (filteredData.length === 0) {
      return;
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();

    const addHeader = () => {
      doc.setFontSize(18);
      doc.text("Raport Payout Alexandra", pageWidth / 2, 15, {
        align: "center",
      });

      doc.setFontSize(11);
      const generatedAt = new Date();
      const generatedAtStr = `${generatedAt.getFullYear()}-${String(
        generatedAt.getMonth() + 1,
      ).padStart(
        2,
        "0",
      )}-${String(generatedAt.getDate()).padStart(2, "0")} ${String(
        generatedAt.getHours(),
      ).padStart(
        2,
        "0",
      )}:${String(generatedAt.getMinutes()).padStart(2, "0")}:${String(
        generatedAt.getSeconds(),
      ).padStart(2, "0")}`;
      doc.text(`Generat la: ${generatedAtStr}`, 14, 25);
      doc.text(`Total payout: ${formatNumber(totalAmount)} lei`, 14, 32);
    };

    const summaryRows = [
      [
        "<= 1.999 lei",
        numProductsUnder2000,
        `20 lei x ${numProductsUnder2000}`,
        `${formatNumber(amountUnder2000)} lei`,
      ],
    ];

    if (numProductsOver2000 > 0) {
      let calcLabel = "";
      if (numProductsOver2000 < 30) {
        calcLabel = `40 lei x ${numProductsOver2000} produse + bonus ${formatNumber(
          bonusValue,
        )}`;
      } else if (numProductsOver2000 < 50) {
        calcLabel = `${formatNumber(
          bonusValue,
        )} pentru primele 30 produse + 40 lei x ${extraProducts} produse peste 30`;
      } else {
        calcLabel = `${formatNumber(
          bonusValue,
        )} pentru primele 50 produse + 40 lei x ${extraProducts} produse peste 50`;
      }

      summaryRows.push([
        ">= 2.000 lei",
        numProductsOver2000,
        calcLabel,
        `${formatNumber(amountOver2000)} lei`,
      ]);
    }

    const buildProductRows = (items) =>
      items.map((item) => [
        item.sku || "-",
        item.summary || "N/A",
        `${formatNumber(Number(item.pretIntrareRON) || 0)} lei`,
        item.status || "-",
        formatDate(item.dataIntrare),
      ]);

    const hexToRgb = (hex) => {
      const sanitized = hex.replace("#", "");
      const bigint = parseInt(sanitized, 16);
      return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
      };
    };

    addHeader();

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `Perioada selectata: ${displayStartDate} - ${displayEndDate}`,
      14,
      39,
    );
    doc.text(
      `Total produse Alexandra: ${formatNumber(totalFilteredProducts)}`,
      14,
      46,
    );
    doc.text(
      `Produse peste 2.000 lei: ${formatNumber(numProductsOver2000)}`,
      14,
      53,
    );

    const segmentStartY = 58;
    const segmentHeight = 12;
    const segmentWidth = 60;
    let currentX = 14;

    tierSegments.forEach((segment) => {
      const rgb = hexToRgb(segment.color);
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(245, 245, 245);
      doc.rect(currentX, segmentStartY, segmentWidth, segmentHeight, "FD");

      const ratio =
        segment.capacity === 0
          ? 0
          : Math.max(Math.min(segment.filled / segment.capacity, 1), 0);

      if (ratio > 0) {
        doc.setFillColor(rgb.r, rgb.g, rgb.b);
        doc.rect(
          currentX,
          segmentStartY,
          segmentWidth * ratio,
          segmentHeight,
          "F",
        );
      }

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.text(
        segment.title,
        currentX + segmentWidth / 2,
        segmentStartY + segmentHeight + 5,
        { align: "center" },
      );
      doc.setFontSize(8);
      doc.text(
        segment.bonus,
        currentX + segmentWidth / 2,
        segmentStartY + segmentHeight + 10,
        { align: "center" },
      );

      currentX += segmentWidth;
    });

    const tierInfoBaseY = segmentStartY + segmentHeight + 17;
    doc.setFontSize(10);
    const currentTierLabel =
      currentTierIndex >= 0
        ? `${tierSegments[currentTierIndex].title} (${tierSegments[currentTierIndex].bonus})`
        : "N/A";
    doc.text(
      `Prag curent: ${currentTierLabel} | ${formatNumber(
        numProductsOver2000,
      )} produse peste 2.000 lei`,
      14,
      tierInfoBaseY,
    );

    const tableStartY = tierInfoBaseY + (nextThreshold !== null ? 12 : 8);

    if (nextThreshold !== null) {
      doc.text(
        `Produse ramase pana la urmatorul prag (${formatNumber(
          nextThreshold,
        )}): ${formatNumber(remainingToNext)}`,
        14,
        tierInfoBaseY + 6,
      );
    }

    autoTable(doc, {
      startY: tableStartY,
      head: [["Prag", "Nr. produse", "Calcul", "Total"]],
      body: summaryRows,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [33, 150, 243], textColor: 255 },
    });

    doc.text(
      `Bonus de plecare inclus: ${formatNumber(bonusValue)} lei`,
      14,
      doc.lastAutoTable.finalY + 8,
    );

    const addProductTable = (title, items) => {
      const previousFinalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 40;
      const startY = previousFinalY + 16;

      doc.setFontSize(13);
      doc.text(title, 14, startY);

      const rows =
        items.length > 0
          ? buildProductRows(items)
          : [["-", "Nu exista produse", "-", "-", "-"]];

      autoTable(doc, {
        startY: startY + 5,
        head: [["SKU", "Produs", "Pret intrare", "Status", "Data intrare"]],
        body: rows,
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [66, 66, 66], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });
    };

    addProductTable(
      `Produse pana la 1.999 lei (${productsUnder2000.length})`,
      productsUnder2000,
    );

    addProductTable(
      `Produse peste 2.000 lei (${productsOver2000.length})`,
      productsOver2000,
    );

    doc.save(`alexandra-payout-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Pregătim datele pentru grafic (doar produsele peste 2000 lei)
  const maxProductsOver2000 = 50;
  const productsRemainingOver2000 =
    maxProductsOver2000 - numProductsOver2000 > 0
      ? maxProductsOver2000 - numProductsOver2000
      : 0;

  const pieData = [
    { name: "Produse peste 2000 lei", value: numProductsOver2000 },
    { name: "Produse ramase", value: productsRemainingOver2000 },
  ];

  const COLORS = ["#0088FE", "#FF0000"]; // Albastru pentru produse adăugate, roșu pentru produse rămase

  // Creăm un sumar al calculelor
  let summaryLines = [];

  if (numProductsUnder2000 > 0) {
    summaryLines.push(
      `20 lei x ${numProductsUnder2000} produse sub 2000 lei = ${formatNumber(
        amountUnder2000,
      )} lei`,
    );
  }

  if (numProductsOver2000 > 0) {
    if (numProductsOver2000 < 30) {
      summaryLines.push(
        `40 lei x ${numProductsOver2000} produse peste 2000 lei = ${formatNumber(
          numProductsOver2000 * 40,
        )} lei`,
      );
      summaryLines.push(`Bonus de plecare: ${formatNumber(bonusValue)} lei`);
    } else if (numProductsOver2000 < 50) {
      summaryLines.push(
        `${formatNumber(
          bonusValue,
        )} lei pentru primele 30 produse peste 2000 lei`,
      );
      if (extraProducts > 0) {
        summaryLines.push(
          `40 lei x ${extraProducts} produse peste 30 = ${formatNumber(
            extraProducts * 40,
          )} lei`,
        );
      }
    } else {
      summaryLines.push(
        `${formatNumber(
          bonusValue,
        )} lei pentru 50 sau mai multe produse peste 2000 lei`,
      );
      if (extraProducts > 0) {
        summaryLines.push(
          `40 lei x ${extraProducts} produse peste 50 = ${formatNumber(
            extraProducts * 40,
          )} lei`,
        );
      }
    }
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6">Alexandra Payout</Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            mt: 1.5,
          }}
        >
          <Box>
            <Typography variant="subtitle2">Perioada selectată</Typography>
            <Typography variant="body2">
              {displayStartDate} - {displayEndDate}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Total produse Alexandra</Typography>
            <Typography variant="body2">
              {formatNumber(totalFilteredProducts)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Produse peste 2000 lei</Typography>
            <Typography variant="body2">
              {formatNumber(numProductsOver2000)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Pragul curent</Typography>
            <Typography variant="body2">
              {currentTierIndex >= 0
                ? tierSegments[currentTierIndex].title
                : "N/A"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            borderRadius: 2,
            p: 2,
            background:
              "linear-gradient(90deg, rgba(25,118,210,0.08), rgba(46,125,50,0.08))",
          }}
        >
          <Typography variant="subtitle1">
            Progres bonus produse peste 2000 lei
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatNumber(numProductsOver2000)} produse peste 2000 lei
          </Typography>
          {nextThreshold !== null ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Mai sunt {formatNumber(remainingToNext)} produse pana la pragul de{" "}
              {formatNumber(nextThreshold)}.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Ai depasit pragul maxim (50+ produse). Continua excelent!
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              mt: 2,
              borderRadius: 1,
              overflow: "hidden",
              boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
            }}
          >
            {tierSegments.map((segment, index) => {
              const ratio =
                segment.capacity === 0
                  ? 0
                  : Math.max(Math.min(segment.filled / segment.capacity, 1), 0);
              const isActive = index <= currentTierIndex;
              return (
                <Box
                  key={segment.title}
                  sx={{
                    flex: segment.capacity,
                    position: "relative",
                    minHeight: 80,
                    backgroundColor: alpha(segment.color, 0.12),
                    borderRight:
                      index < tierSegments.length - 1
                        ? "1px solid rgba(0,0,0,0.06)"
                        : "none",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: `${ratio * 100}%`,
                      backgroundColor: segment.color,
                      opacity: isActive ? 0.8 : 0.35,
                      transition: "width 0.4s ease",
                    }}
                  />
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 1.5,
                      textAlign: "center",
                      color: isActive && ratio > 0 ? "#ffffff" : "text.primary",
                    }}
                  >
                    <Typography variant="subtitle2">{segment.title}</Typography>
                    <Typography variant="caption">{segment.bonus}</Typography>
                    {index === 0 && (
                      <Typography variant="caption" sx={{ mt: 0.5 }}>
                        {formatNumber(Math.min(numProductsOver2000, 30))} / 30
                      </Typography>
                    )}
                    {index === 1 && (
                      <Typography variant="caption" sx={{ mt: 0.5 }}>
                        {formatNumber(
                          Math.max(Math.min(numProductsOver2000 - 30, 20), 0),
                        )}{" "}
                        / 20
                      </Typography>
                    )}
                    {index === 2 && (
                      <Typography variant="caption" sx={{ mt: 0.5 }}>
                        {formatNumber(Math.max(numProductsOver2000 - 50, 0))}{" "}
                        produse extra
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            width: 400,
            height: 400,
            margin: "0 auto",
            mt: 3,
          }}
        >
          <PieChart width={400} height={400}>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={100}
              fill="#8884d8"
              paddingAngle={2}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
          {/* Afișăm suma în centrul  graficului */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <Typography variant="h5">
              {formatNumber(totalAmount)} lei
            </Typography>
          </Box>
        </Box>
        {/* Afișăm sumarul  */}
        {summaryLines.map((line, index) => (
          <Typography key={index} variant="body2">
            {line}
          </Typography>
        ))}
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={generatePdfReport}
            disabled={filteredData.length === 0}
          >
            Exportă raport PDF
          </Button>
        </Box>
        <Box mt={2}>
          <Typography variant="subtitle2">
            Bonus de plecare inclus: {formatNumber(bonusValue)} lei
          </Typography>
        </Box>
        <Box mt={2}>
          <Typography variant="subtitle1">
            Produse până la 1999 lei ({productsUnder2000.length})
          </Typography>
          {productsUnder2000.length === 0 ? (
            <Typography variant="body2">
              Nu există produse în acest interval.
            </Typography>
          ) : (
            productsUnder2000.map((item) => (
              <Typography
                key={`${item.sku}-${item.nrJurnalSpecial || item.summary}`}
                variant="body2"
              >
                {item.summary || item.sku} —{" "}
                {formatNumber(Number(item.pretIntrareRON) || 0)} lei (intrare:{" "}
                {formatDate(item.dataIntrare)})
              </Typography>
            ))
          )}
        </Box>
        <Box mt={2}>
          <Typography variant="subtitle1">
            Produse peste 2000 lei ({productsOver2000.length})
          </Typography>
          {productsOver2000.length === 0 ? (
            <Typography variant="body2">
              Nu există produse în acest interval.
            </Typography>
          ) : (
            productsOver2000.map((item) => (
              <Typography
                key={`${item.sku}-${item.nrJurnalSpecial || item.summary}`}
                variant="body2"
              >
                {item.summary || item.sku} —{" "}
                {formatNumber(Number(item.pretIntrareRON) || 0)} lei (intrare:{" "}
                {formatDate(item.dataIntrare)})
              </Typography>
            ))
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AlexandraPayout;
